import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import DeleteConfirmDialog from '@/components/dialogs/DeleteConfirmDialog';
import type { PublicSession } from '@arrowise/shared';
import { Pencil, Trash2, Eye, Mail, Download } from 'lucide-react';
import sessionService from '@/services/sessionService';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import supportService from '@/services/supportService';
import { toast } from 'sonner';

export interface SessionListItemProps {
    session: PublicSession;
    onManage?: (id: string) => void;
    onDelete?: (id: string) => void;
    onView?: (id: string) => void;
    canManageSession?: (session: PublicSession) => boolean;
    isFreeUser?: boolean;
}

export default function SessionListItem({ session, onManage, onDelete, onView, canManageSession, isFreeUser }: SessionListItemProps) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [resendError, setResendError] = useState<string | null>(null);
    const [exportLoading, setExportLoading] = useState(false);
    const when = session.dueAt ? new Date(session.dueAt) : null;
    const formattedWhen = when
        ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(when)
        : 'No due date';
    const endedAt = session.endedAt ? new Date(session.endedAt) : null;
    const formattedEndedAt = endedAt
        ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(endedAt)
        : 'No end date';
    const canManage = canManageSession ? canManageSession(session) : true; // Default to true for backward compatibility
    const isComplete = !!session.endedAt;
    const isReady = !!(isComplete && session.replay?.ready);
    const isFailed = !!(isComplete && session.replay?.error);
    const isPastDue = !!(session.dueAt && !isComplete && new Date(session.dueAt) < new Date());
    const resendTooltip = isPastDue
        ? "The session is past due and the candidate can no longer join. Update the due date to resend the invite, or create a new session."
        : "If the candidate already started, this invite won’t let them rejoin. To restart, create a new session.";
    const isStarted = !!session.startedAt && !isComplete;
    const buildFailureBadgeMessage = 'Problem building session replay. Click to send support ticket.'
    const [buildErrorText, setBuildErrorText] = useState<string>(buildFailureBadgeMessage);

    const sendSupportTicket = async () => {
        if (buildErrorText !== buildFailureBadgeMessage) return;
        setBuildErrorText('Sending support ticket...');
        try {
            await supportService.submitSessionBuildFailureTicket(session.id, null, session.teamId);
            setBuildErrorText('Support ticket sent! We will follow up by email.');
        } catch (error: any) {
            // if 429 use sonner toast
            if (error.response?.status === 429) {
                toast.error('Too many requests sent');
            }
            console.error('Failed to send support ticket:', error);
        } finally {
            setTimeout(() => setBuildErrorText(buildFailureBadgeMessage), 2000);
        }

    };

    const handleResendInvite = async () => {
        setResendLoading(true);
        setResendSuccess(false);
        setResendError(null);
        try {
            await sessionService.resendCandidateInviteEmail(session.id);
            setResendSuccess(true);
        } catch (err: any) {
            setResendError(err.message || 'Failed to resend invite');
        } finally {
            setResendLoading(false);
            setTimeout(() => setResendSuccess(false), 2000);
        }
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            await sessionService.exportSession(session.id);
            toast.success('Preparing download...');
        } catch (err: any) {
            const msg = err?.message || 'Failed to export session';
            toast.error(msg);
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
                <div className="space-y-1">
                    <CardTitle className="text-base">{session.candidateName}, ({session.candidateEmail})</CardTitle>
                    <CardDescription className="flex items-center gap-2">
                        {isComplete ?
                            <div>
                                Completed on {formattedEndedAt}
                            </div>
                            :
                            <div>
                                Due on {formattedWhen}
                            </div>}
                        <Separator orientation="vertical" className="h-4" />
                        {isPastDue && <Badge variant="destructive">Past due</Badge>}
                        {isStarted && <Badge variant="secondary">In Progress</Badge>}
                        {isComplete && <Badge variant="default">Complete</Badge>}
                        {session.graded && <Badge className="bg-green-600 text-white border-green-600">Assessment complete</Badge>}
                    </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                    {/* Export/download zip (always available when session complete) */}
                    {isComplete && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button size="sm" variant="ghost" onClick={handleExport} disabled={exportLoading}>
                                    <Download className="size-4 mr-1" />
                                    {exportLoading ? 'Preparing...' : 'Download'}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent sideOffset={6}>
                                Download session files and AI interactions as a zip.
                            </TooltipContent>
                        </Tooltip>
                    )}
                    {isComplete && (
                        isFailed ? (
                            <Badge className='cursor-pointer' onClick={sendSupportTicket} variant="destructive"
                            >
                                {buildErrorText}
                            </Badge>
                        ) : (isReady || (isFreeUser && isComplete)) ? (
                            onView ? (
                                <Button size="sm" variant="outline" onClick={() => onView(session.id)}>
                                    <Eye className="size-4 mr-1" /> View
                                </Button>
                            ) : null
                        ) : (
                            <Badge variant="secondary">Preparing…</Badge>
                        )
                    )}
                    {!isComplete && canManage && session.candidateEmail && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span>
                                    <Button size="sm" variant="outline" onClick={handleResendInvite} disabled={resendLoading || isPastDue}>
                                        <Mail className="size-4 mr-1" />
                                        {resendLoading ? 'Sending...' : resendSuccess ? 'Sent!' : 'Re-send invite'}
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent sideOffset={6}>
                                {resendTooltip}
                            </TooltipContent>
                        </Tooltip>
                    )}
                    {!isComplete && onManage && canManage && (
                        <Button size="sm" variant="outline" onClick={() => onManage(session.id)}>
                            <Pencil className="size-4 mr-1" /> Manage
                        </Button>
                    )}
                    {!isComplete && onDelete && canManage && (
                        <>
                            <Button size="sm" variant="destructive" onClick={() => setConfirmOpen(true)}>
                                <Trash2 className="size-4 mr-1" /> Delete
                            </Button>
                            <DeleteConfirmDialog
                                open={confirmOpen}
                                onOpenChange={setConfirmOpen}
                                title="Delete session?"
                                description="This action cannot be undone. This will permanently delete the session."
                                onConfirm={() => onDelete && onDelete(session.id)}
                                confirmLabel="Delete"
                                cancelLabel="Cancel"
                                confirmVariant="destructive"
                            />
                        </>
                    )}
                </div>
                {resendError && <div className="text-xs text-destructive mt-2">{resendError}</div>}
            </CardHeader>
        </Card>
    );
}
